import React from 'react';
import { PageView } from '../types';
import { 
  ShieldCheck, 
  ArrowRight, 
  FileText, 
  Sliders, 
  CheckCircle2, 
  Lock, 
  Server, 
  Zap, 
  ChevronDown
} from 'lucide-react';

interface OwnerLandingPageProps {
  setActivePage?: (page: PageView) => void;
  onOpenLeadModal: () => void;
}

export const OwnerLandingPage: React.FC<OwnerLandingPageProps> = ({ onOpenLeadModal }) => {
  const scrollToHowItWorks = (e: React.MouseEvent) => {
    e.preventDefault();
    const el = document.getElementById('how-it-works');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="owner-landing-page">
      {/* 1. ABOVE THE FOLD (HERO) */}
      <section className="landing-hero-section">
        <div className="section-container">
          <div className="hero-content-center">
            <h1 className="hero-title">
              Replace three vendor dashboards with one platform built for your network size.
            </h1>

            <p className="hero-subtitle">
              Stop paying enterprise software fees across mixed DOCSIS, PON, and wireless gear. 
              Get a single operational view across every vendor and customer, priced per subscriber, with zero hardware replacement.
            </p>

            <div className="cta-wrapper">
              <button onClick={onOpenLeadModal} className="btn btn-primary btn-hero-lg">
                <span>Get Your Network-Consolidation Review</span>
                <ArrowRight size={16} />
              </button>

              <p className="cta-microcopy">
                15 minutes: we'll map what MUSE replaces across your vendor stack and explain how per-subscriber pricing works for your size. No sales pressure.
              </p>
            </div>

            <div className="credibility-microline">
              <ShieldCheck size={14} className="text-muted-icon" />
              <span>Built by engineers with 30+ years running networks (America Online, early DSL, internet peering), designed specifically for Tier 3 and 4 operators.</span>
            </div>

            <div className="hero-anchor-link">
              <a href="#how-it-works" onClick={scrollToHowItWorks} className="anchor-subtle">
                <span>See how consolidation works</span>
                <ChevronDown size={14} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* 1a. ARCHITECTURAL TRUST STRIP (Top 1/3) */}
      <section className="trust-strip-section">
        <div className="section-container">
          <div className="trust-banner-card">
            <div className="trust-icon-badge">
              <Lock size={18} />
            </div>
            <div className="trust-banner-body">
              <h3>Your Network Stays Yours: Zero Inbound Access, Complete Data Isolation</h3>
              <p>
                MUSE sits behind your firewall using an outbound-only collector (zero inbound ports, no public IP, no NAT changes) 
                and stores telemetry in a 100% single-tenant cloud project. Your topology, credentials, and customer data remain strictly isolated to your ISP.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 2. THE VILLAIN: COST OF STATUS QUO */}
      <section className="section-padding border-top">
        <div className="section-container">
          <div className="section-heading text-center">
            <h2>The Hidden Cost of Native Vendor Dashboards</h2>
            <p>Running separate platforms for Calix, Nokia, Adtran, or Casa eats into margins and locks you into vendor pricing.</p>
          </div>

          <div className="contrast-grid">
            {/* Status Quo */}
            <div className="contrast-card villain-card">
              <div className="contrast-card-header">
                <span className="status-dot red" />
                <span className="contrast-label font-mono">STATUS QUO</span>
              </div>
              <h3>Native Vendor Dashboards</h3>
              <ul className="contrast-list">
                <li>
                  <div className="list-title">Enterprise Pricing for Tier 3/4 Budgets</div>
                  <div className="list-desc">Paying top-tier software licenses for every hardware slice as you expand your footprint.</div>
                </li>
                <li>
                  <div className="list-title">Operational Blind Spots Across Platforms</div>
                  <div className="list-desc">NOC teams forced to jump between browser tabs during outages to track issues across DOCSIS and PON.</div>
                </li>
                <li>
                  <div className="list-title">Eroded Hardware Negotiating Leverage</div>
                  <div className="list-desc">Locked into buying hardware from one vendor because adding a new vendor means adding another dashboard.</div>
                </li>
              </ul>
            </div>

            {/* The MUSE Approach */}
            <div className="contrast-card hero-style-card">
              <div className="contrast-card-header">
                <span className="status-dot green" />
                <span className="contrast-label font-mono">THE MUSE APPROACH</span>
              </div>
              <h3>Agnostic Operational Intelligence</h3>
              <ul className="contrast-list">
                <li>
                  <div className="list-title">Per-Subscriber Rates Sized for Small Operators</div>
                  <div className="list-desc">One predictable software cost across your entire subscriber base, regardless of vendor mix.</div>
                </li>
                <li>
                  <div className="list-title">Unified Visibility Across Every Vendor</div>
                  <div className="list-desc">Outside-in customer experience combined with inside-out core health on a single dashboard.</div>
                </li>
                <li>
                  <div className="list-title">Uncompromising Hardware Freedom</div>
                  <div className="list-desc">Bid gear freely across vendors without losing control or adding standalone software platforms.</div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 3. THE REFRAME: WHAT MUSE DOES */}
      <section className="section-padding border-top">
        <div className="section-container">
          <div className="section-heading text-center">
            <h2>Operational Intelligence Built for the Bottom Line</h2>
            <p>Clear visibility, predictable costs, and cleaner reporting for grant-funded and independent ISPs.</p>
          </div>

          <div className="grid-3">
            {/* Card 1: Tooling Cost */}
            <div className="flat-card benefit-card">
              <div className="benefit-icon-header">
                <div className="icon-wrapper">
                  <Zap size={20} />
                </div>
                <span className="pillar-tag font-mono">PRIORITY A</span>
              </div>
              <h3>Consolidate Your Monitoring Stack into One Predictable Cost</h3>
              <p>
                Eliminate redundant software licenses by replacing vendor-specific dashboards with one unified operational platform. 
                You pay a simple per-subscriber rate built for independent operators, keeping software overhead low as you execute your build-out plan.
              </p>
            </div>

            {/* Card 2: Reporting */}
            <div className="flat-card benefit-card">
              <div className="benefit-icon-header">
                <div className="icon-wrapper">
                  <FileText size={20} />
                </div>
                <span className="pillar-tag font-mono">PRIORITY B</span>
              </div>
              <h3>One Single Operational Record for Grant &amp; Funder Reporting</h3>
              <p>
                Maintain an accurate record of network availability, uptime trends, and outage impacts across your entire footprint. 
                Having one consolidated telemetry history makes it straightforward to pull the operational performance data owed to BEAD grant administrators and investors.
              </p>
            </div>

            {/* Card 3: Hardware Freedom */}
            <div className="flat-card benefit-card">
              <div className="benefit-icon-header">
                <div className="icon-wrapper">
                  <Sliders size={20} />
                </div>
                <span className="pillar-tag font-mono">PRIORITY C</span>
              </div>
              <h3>Maintain Negotiating Power on Every Hardware Bid</h3>
              <p>
                Select access hardware based on price, delivery timelines, and performance rather than software lock-in. 
                MUSE Telemetry works with equipment you already run and onboards new device types in minutes via MUSE Forge, preserving your leverage in vendor negotiations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 4. HOW IT WORKS */}
      <section id="how-it-works" className="section-padding border-top">
        <div className="section-container">
          <div className="section-heading text-center">
            <h2>White-Glove Setup Without Network Disruption</h2>
            <p>We handle the setup so your team can focus on expanding subscriber lines.</p>
          </div>

          <div className="steps-grid">
            <div className="step-card">
              <div className="step-num font-mono">01</div>
              <h4>Share your inventory</h4>
              <p>You supply your IP ranges, hardware types, and service accounts. No manual data entry or complex mapping required.</p>
            </div>

            <div className="step-card">
              <div className="step-num font-mono">02</div>
              <h4>Deploy the collector appliance</h4>
              <p>We ship a pre-configured hardware appliance or stand up a Docker container behind your firewall. <em>Outbound-only connection guarantees zero open inbound ports and zero NAT changes.</em></p>
            </div>

            <div className="step-card">
              <div className="step-num font-mono">03</div>
              <h4>Configure with a dedicated engineer</h4>
              <p>An assigned technician assists your team step-by-step with poller configuration, device discovery, and alert threshold setting.</p>
            </div>

            <div className="step-card">
              <div className="step-num font-mono">04</div>
              <h4>Gain full-network visibility</h4>
              <p>Monitor customer experience trends, correlated root-cause alerts, and configuration history in one dashboard. <em>All telemetry is secured in a 100% single-tenant cloud environment isolated strictly to your company.</em></p>
            </div>
          </div>
        </div>
      </section>

      {/* 5. WHY TRUST A NEW PLATFORM */}
      <section className="section-padding border-top">
        <div className="section-container">
          <div className="flat-card founder-story-card">
            <div className="founder-card-badge">
              <Server size={20} />
              <span>OPERATOR PEDIGREE</span>
            </div>
            <h2>Built by Engineers Who Managed Networks for 30+ Years</h2>
            <p className="founder-lead">
              No marketing hype or unverified claims, just software built by operators for operators.
            </p>
            <div className="founder-body-text">
              <p>
                Independent ISPs don't have time for vendor spin or bloated enterprise software. MUSE Telemetry was founded by network engineering leaders with over 30 years of experience building and operating infrastructure, scaling early commercial internet platforms (America Online), managing DSL providers, expanding peering points, and building custom ISP monitoring from scratch (including converting an ATM business into an ISP that was later acquired by MegaPath).
              </p>
              <p>
                When you partner with MUSE Telemetry, you don't get handed off to junior account managers or sales reps. <strong>You work directly with the senior infrastructure architect who built the platform.</strong>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 6. RISK REVERSAL: FOUNDER-LED PILOT OFFER */}
      <section className="section-padding border-top">
        <div className="section-container">
          <div className="flat-card offer-highlight-card">
            <div className="section-heading text-center mb-4">
              <h2>Evaluate MUSE on Your Live Network Traffic</h2>
              <p>Verify root-cause clustering, hardware consolidation, and multi-vendor telemetry on your production gear.</p>
            </div>

            <div className="offer-points-grid">
              <div className="offer-point font-body">
                <CheckCircle2 size={20} className="check-icon" />
                <div>
                  <strong>Live Production Pilot</strong>
                  <p>Deploy the collector appliance on a segment of your actual DOCSIS, PON, or wireless nodes to test performance in your production environment.</p>
                </div>
              </div>

              <div className="offer-point font-body">
                <CheckCircle2 size={20} className="check-icon" />
                <div>
                  <strong>Hands-On Forge Sandbox</strong>
                  <p>Explore device schema creation using real diagnostic commands in our interactive in-browser demo playground.</p>
                </div>
              </div>

              <div className="offer-point font-body">
                <CheckCircle2 size={20} className="check-icon" />
                <div>
                  <strong>Founder-Led Onboarding</strong>
                  <p>The engineer who built the platform assists your team directly with poller configuration, device discovery, and alert tuning.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 7. CLOSE: FINAL CTA */}
      <section className="section-padding border-top pb-6">
        <div className="section-container">
          <div className="flat-card cta-card-center owner-closing-card">
            <h2>See What Your Monitoring Stack Could Consolidate To</h2>
            <p className="cta-lead">
              In a quick 15-minute consultation, we'll map your current vendor dashboards, show how MUSE unifies your specific hardware mix, and explain per-subscriber pricing for your subscriber count.
            </p>

            <div className="cta-actions flex-col-center">
              <button onClick={onOpenLeadModal} className="btn btn-primary btn-hero-lg">
                <span>Get Your Network-Consolidation Review</span>
                <ArrowRight size={16} />
              </button>
              <p className="cta-microcopy mt-3">
                15 minutes: map your stack and review per-subscriber pricing for your size. Low-pressure conversation with a peer engineer.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* STYLES FOR LANDING PAGE */}
      <style>{`
        .owner-landing-page {
          width: 100%;
        }

        .landing-hero-section {
          padding: 5.5rem 0 3.5rem;
          text-align: center;
        }

        .hero-content-center {
          max-width: 860px;
          margin: 0 auto;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .hero-title {
          font-size: 2.75rem;
          font-weight: 800;
          color: #ffffff;
          line-height: 1.18;
          letter-spacing: -0.035em;
          margin-bottom: 1.25rem;
        }

        .hero-subtitle {
          font-size: 1.15rem;
          color: #999999;
          line-height: 1.6;
          margin-bottom: 2rem;
          max-width: 760px;
        }

        .cta-wrapper {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.75rem;
          margin-bottom: 1.75rem;
        }

        .btn-hero-lg {
          padding: 0.85rem 1.85rem;
          font-size: 1rem;
          font-weight: 600;
          border-radius: var(--radius-sm);
        }

        .cta-microcopy {
          font-size: 0.82rem;
          color: var(--text-muted);
          max-width: 540px;
          line-height: 1.45;
          text-align: center;
        }

        .credibility-microline {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          font-size: 0.8rem;
          color: var(--text-dim);
          max-width: 680px;
          margin-bottom: 2rem;
          line-height: 1.4;
        }

        .text-muted-icon {
          color: var(--text-dim);
          flex-shrink: 0;
        }

        .hero-anchor-link {
          margin-top: 0.5rem;
        }

        .anchor-subtle {
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
          color: var(--text-muted);
          font-size: 0.84rem;
          text-decoration: none;
          transition: color 0.15s ease;
        }

        .anchor-subtle:hover {
          color: #ffffff;
        }

        /* TRUST BANNER */
        .trust-strip-section {
          padding: 0.5rem 0 2.5rem;
        }

        .trust-banner-card {
          background: #06090e;
          border: 1px solid #1e293b;
          border-radius: var(--radius-md);
          padding: 1.35rem 1.75rem;
          display: flex;
          align-items: flex-start;
          gap: 1.25rem;
          box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.05);
        }

        .trust-icon-badge {
          background: rgba(37, 99, 235, 0.12);
          color: #3b82f6;
          padding: 0.65rem;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          border: 1px solid rgba(59, 130, 246, 0.25);
        }

        .trust-banner-body h3 {
          font-size: 0.98rem;
          font-weight: 600;
          color: #ffffff;
          margin-bottom: 0.35rem;
        }

        .trust-banner-body p {
          font-size: 0.85rem;
          color: #94a3b8;
          line-height: 1.5;
          margin: 0;
        }

        /* CONTRAST GRID (THE VILLAIN) */
        .contrast-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 2rem;
          margin-top: 2rem;
        }

        .contrast-card {
          background: var(--bg-card);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-md);
          padding: 2.25rem;
        }

        .contrast-card.villain-card {
          background: #080404;
          border-color: #2a1414;
        }

        .contrast-card.hero-style-card {
          background: #040806;
          border-color: #12281b;
        }

        .contrast-card-header {
          display: flex;
          align-items: center;
          gap: 0.6rem;
          margin-bottom: 1rem;
        }

        .contrast-label {
          font-size: 0.72rem;
          letter-spacing: 0.05em;
          color: var(--text-dim);
        }

        .contrast-card h3 {
          font-size: 1.35rem;
          margin-bottom: 1.5rem;
        }

        .contrast-list {
          list-style: none;
          display: flex;
          flex-direction: column;
          gap: 1.25rem;
        }

        .contrast-list li {
          display: flex;
          flex-direction: column;
          gap: 0.2rem;
        }

        .list-title {
          font-weight: 600;
          font-size: 0.95rem;
          color: var(--text-primary);
        }

        .list-desc {
          font-size: 0.85rem;
          color: var(--text-muted);
          line-height: 1.45;
        }

        /* BENEFIT CARDS */
        .benefit-card {
          display: flex;
          flex-direction: column;
          height: 100%;
        }

        .benefit-icon-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 1.25rem;
        }

        .icon-wrapper {
          background: #141414;
          border: 1px solid var(--border-color);
          color: #ffffff;
          padding: 0.5rem;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .pillar-tag {
          font-size: 0.7rem;
          color: var(--text-dim);
        }

        .benefit-card h3 {
          font-size: 1.15rem;
          margin-bottom: 0.75rem;
          line-height: 1.35;
        }

        .benefit-card p {
          font-size: 0.88rem;
          line-height: 1.55;
        }

        /* STEPS GRID */
        .steps-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 1.25rem;
          margin-top: 2rem;
        }

        .step-card {
          background: var(--bg-card);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-md);
          padding: 1.75rem 1.35rem;
          display: flex;
          flex-direction: column;
        }

        .step-num {
          font-size: 1.4rem;
          font-weight: 700;
          color: var(--text-dim);
          margin-bottom: 0.75rem;
        }

        .step-card h4 {
          font-size: 0.98rem;
          margin-bottom: 0.5rem;
        }

        .step-card p {
          font-size: 0.82rem;
          line-height: 1.5;
          color: var(--text-muted);
        }

        .step-card em {
          color: var(--text-primary);
          font-style: normal;
        }

        /* FOUNDER STORY CARD */
        .founder-story-card {
          background: #090909;
          border: 1px solid var(--border-subtle);
          padding: 3rem 2.5rem;
        }

        .founder-card-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.75rem;
          font-weight: 600;
          color: var(--text-muted);
          margin-bottom: 1rem;
          letter-spacing: 0.05em;
        }

        .founder-story-card h2 {
          font-size: 2rem;
          margin-bottom: 0.75rem;
        }

        .founder-lead {
          font-size: 1.1rem;
          color: var(--text-primary);
          margin-bottom: 1.75rem;
        }

        .founder-body-text {
          display: flex;
          flex-direction: column;
          gap: 1rem;
          font-size: 0.95rem;
          line-height: 1.7;
          color: var(--text-muted);
          max-width: 900px;
        }

        /* OFFER HIGHLIGHT */
        .offer-highlight-card {
          background: linear-gradient(180deg, #0a0a0a 0%, #05080c 100%);
          border: 1px solid #1e293b;
          padding: 3rem 2.5rem;
        }

        .offer-points-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 2rem;
          margin-top: 1rem;
        }

        .offer-point {
          display: flex;
          gap: 0.85rem;
          align-items: flex-start;
        }

        .check-icon {
          color: var(--dot-green);
          flex-shrink: 0;
          margin-top: 0.15rem;
        }

        .offer-point strong {
          display: block;
          color: #ffffff;
          font-size: 0.98rem;
          margin-bottom: 0.35rem;
        }

        .offer-point p {
          font-size: 0.85rem;
          line-height: 1.5;
          margin: 0;
        }

        /* CLOSING CTA CARD */
        .owner-closing-card {
          padding: 3.5rem 2rem;
        }

        .flex-col-center {
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .mb-3 { margin-bottom: 0.75rem; }
        .mb-4 { margin-bottom: 1.5rem; }
        .mt-3 { margin-top: 0.75rem; }

        @media (max-width: 950px) {
          .contrast-grid, .steps-grid, .offer-points-grid {
            grid-template-columns: 1fr;
          }
          .hero-title { font-size: 2.1rem; }
          .founder-story-card h2 { font-size: 1.6rem; }
        }
      `}</style>
    </div>
  );
};

export default OwnerLandingPage;
